import { useSyncExternalStore } from "react";

// ============================================================================
// CRM Chat — quick replies / canned responses store (front-end only).
//
// A small shared catalog of reusable WhatsApp replies. Managed from
// /crm/setting/chat (CRUD) and consumed in /crm/tareas/chat as one-tap chips.
// Persisted to localStorage so edits survive reloads. Single source of truth so
// the settings screen and the chat composer never drift apart.
// ============================================================================

const STORAGE_KEY = "crm.quickReplies.v1";

export const QR_CATEGORIES = [
  "Saludo",
  "Seguimiento",
  "Cierre",
  "Soporte",
  "Agenda",
  "General",
] as const;
export type QuickReplyCategory = (typeof QR_CATEGORIES)[number];

export interface QuickReply {
  id: string;
  title: string; // short label shown on the chip / in the list
  text: string; // the actual message body inserted into the composer
  category: QuickReplyCategory;
  /** Optional "/" shortcut, e.g. "/saludo" — typed in the composer to insert. */
  shortcut?: string;
}

export type QuickReplyDraft = Omit<QuickReply, "id"> & { id?: string };

const seed: QuickReply[] = [
  { id: "qr1", title: "Saludo inicial", category: "Saludo", shortcut: "/hola", text: "¡Hola! Gracias por contactarnos 🙌 ¿En qué puedo ayudarte?" },
  { id: "qr2", title: "Pedir correo", category: "Seguimiento", shortcut: "/correo", text: "Con gusto te comparto una propuesta. ¿Cuál es tu correo para enviártela?" },
  { id: "qr3", title: "Agendar llamada", category: "Agenda", shortcut: "/llamada", text: "¿Te parece si agendamos una llamada de 15 min esta semana? ¿Qué día te queda mejor?" },
  { id: "qr4", title: "Despedida", category: "Cierre", shortcut: "/gracias", text: "Quedo atento a cualquier duda. ¡Saludos! 👋" },
  { id: "qr5", title: "Precios", category: "General", shortcut: "/precios", text: "Nuestros planes se adaptan a tu operación. ¿Cuántos usuarios/unidades manejas para darte un precio exacto?" },
  { id: "qr6", title: "Seguimiento sin respuesta", category: "Seguimiento", shortcut: "/seguimiento", text: "Hola, sólo quería dar seguimiento a tu solicitud. ¿Sigues interesado? Estoy para ayudarte." },
];

// --- Store ------------------------------------------------------------------

let replies: QuickReply[] = load();
const listeners = new Set<() => void>();

function load(): QuickReply[] {
  if (typeof window === "undefined") return seed;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return seed;
    const parsed = JSON.parse(raw) as QuickReply[];
    if (Array.isArray(parsed)) return parsed; // allow empty (user cleared the list)
  } catch {
    /* ignore */
  }
  return seed;
}

function persist() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(replies));
  } catch {
    /* storage unavailable */
  }
}

function emit() {
  persist();
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): QuickReply[] {
  return replies;
}

export function useQuickReplies(): QuickReply[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

let seq = 0;
function newId(): string {
  seq += 1;
  return `qr-${(replies.length + seq).toString(36)}-${seq.toString(36)}`;
}

// --- Mutators ---------------------------------------------------------------

export function addQuickReply(draft: QuickReplyDraft): QuickReply {
  const reply: QuickReply = { ...draft, id: draft.id ?? newId() };
  replies = [...replies, reply];
  emit();
  return reply;
}

export function updateQuickReply(id: string, patch: Partial<QuickReply>): void {
  replies = replies.map((r) => (r.id === id ? { ...r, ...patch } : r));
  emit();
}

export function removeQuickReply(id: string): void {
  replies = replies.filter((r) => r.id !== id);
  emit();
}

export function resetQuickReplies(): void {
  replies = seed;
  emit();
}

/** Find a reply whose shortcut matches the given "/command" (case-insensitive). */
export function matchShortcut(token: string): QuickReply | undefined {
  const t = token.trim().toLowerCase();
  if (!t.startsWith("/")) return undefined;
  return replies.find((r) => r.shortcut?.toLowerCase() === t);
}
